import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseImage } from "~/components/common/base-image";
import { useResponsiveImage } from "~/components/common/base-image/hooks";
import { ContentPlaceholder } from "~/components/content/content-placeholder";
import type { ResponsiveVariants } from "~/lib/styles/responsive";
import type { contentCoverImageContent$key } from "~/queries/__generated__/contentCoverImageContent.graphql";

import { Figure } from "./styles";

type Props = {
  className?: string;
  reference: contentCoverImageContent$key;
  responsiveVariants: ResponsiveVariants<
    | "size1200x800"
    | "size1280x720"
    | "size540x360"
    | "size80x80"
    | "size768x768"
    | "size320x240"
    | "size400x400"
  >;
};

export const ContentCoverImage: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  responsiveVariants,
}) => {
  const content = useFragment(
    graphql`
      fragment contentCoverImageContent on Content {
        images {
          url
          title
          type
          size1200x800: style(filter: { style: "1200x800" }) {
            url
          }
          size1280x720: style(filter: { style: "wide_landscape" }) {
            url
          }
          size768x768: style(filter: { style: "768x768" }) {
            url
          }
          size540x360: style(filter: { style: "540x360" }) {
            url
          }
          size320x240: style(filter: { style: "320x240" }) {
            url
          }
          size80x80: style(filter: { style: "80x80" }) {
            url
          }
          size400x400: style(filter: { style: "crop_400x400" }) {
            url
          }
        }
      }
    `,
    reference_,
  );

  const mainImage =
    content.images?.find(image => image?.type === "mainImage") ?? first(content.images);

  const variantSpecs = {
    size1200x800: { aspectRatio: "3/2", url: mainImage?.size1200x800?.url, width: 1200 },
    size1280x720: { aspectRatio: "16/9", url: mainImage?.size1280x720?.url, width: 1280 },
    size320x240: { aspectRatio: "4/3", url: mainImage?.size320x240?.url, width: 320 },
    size400x400: { aspectRatio: "1/1", url: mainImage?.size400x400?.url, width: 400 },
    size540x360: { aspectRatio: "3/2", url: mainImage?.size540x360?.url, width: 540 },
    size768x768: { aspectRatio: "1/1", url: mainImage?.size768x768?.url, width: 768 },
    size80x80: { aspectRatio: "1/1", url: mainImage?.size80x80?.url, width: 80 },
  };

  const mapVariant = (viewportSize: "desktopUp" | "tabletUp" | "mobileUp") => ({
    aspectRatio: variantSpecs[responsiveVariants[viewportSize]].aspectRatio,
    url: variantSpecs[responsiveVariants[viewportSize]].url ?? "",
    width: variantSpecs[responsiveVariants[viewportSize]].width,
  });

  const responsiveVariant = {
    desktopUp: mapVariant("desktopUp"),
    mobileUp: mapVariant("mobileUp"),
    tabletUp: mapVariant("tabletUp"),
  };

  const { bindResponsiveAspectRatio } = useResponsiveImage(responsiveVariant);

  return (
    <Figure {...bindResponsiveAspectRatio}>
      {mainImage?.url ? (
        <BaseImage
          $objectFit="cover"
          alt={mainImage?.title ?? undefined}
          className={className}
          responsiveVariant={responsiveVariant}
          src={mainImage?.url}
          {...bindResponsiveAspectRatio}
          lazyload
        />
      ) : (
        <ContentPlaceholder {...bindResponsiveAspectRatio} />
      )}
    </Figure>
  );
};

ContentCoverImage.displayName = "ContentCoverImage";
